import React, { useEffect, useState } from 'react'
import { Cloud, X, Loader, Check, Upload } from 'lucide-react'
import { Button } from '../../ui/Button'
import {
  documentService,
  DocumentType,
  type DocumentUploadItem,
} from '../../../services/documentService'
import toast from 'react-hot-toast'
import companyService from '../../../services/companyService'
import { authService } from '../../../services/authService'
import apiClient from '../../../services/api'
import ContinueLaterModal from '../ContinueLaterModal'

type FileStatus = 'empty' | 'pending' | 'uploaded' | 'error'

interface DocumentFile {
  name: string
  status: FileStatus
  error?: string
  file?: File
  documentId?: string
}

interface DocumentationStepProps {
  onNext: () => void
  onCancel: () => void
  personType?: string | null
}

export const DocumentationStep: React.FC<DocumentationStepProps> = ({
  onNext,
  personType,
}) => {
  const [currentPart, setCurrentPart] = useState<1 | 2>(1)
  const [isUploading, setIsUploading] = useState(false)
  const [isNavigating, setIsNavigating] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [isOperativeAddressSameAsFiscal, setIsOperativeAddressSameAsFiscal] =
    useState(true)

  // Load existing company data on component mount
  useEffect(() => {
    if (personType === 'PFAE') return

    const loadExistingData = async () => {
      try {
        console.log('Loading existing company data...')

        // Get existing companies for the user
        const existingCompanies = await companyService.getCompanies()
        console.log('API Response:', existingCompanies)

        if (existingCompanies.length > 0) {
          const company = existingCompanies[0]
          console.log('Found existing company data:', company)
          setIsOperativeAddressSameAsFiscal(
            company.operativeSameAsFiscalAddress || false
          )
        } else {
          console.log('No existing company data found')
        }
      } catch (error) {
        console.error('Error loading existing data:', error)
        // Don't show error toast for missing data, just log it
      }
    }

    loadExistingData()
  })

  // Part 1: Legal Documents
  const [actaConstitutiva, setActaConstitutiva] = useState<DocumentFile>({
    name: '',
    status: 'empty',
  })

  const [comprobanteDomicilio, setComprobanteDomicilio] =
    useState<DocumentFile>({
      name: '',
      status: 'empty',
    })

  const [comprobanteDomicilioOperativo, setComprobanteDomicilioOperativo] =
    useState<DocumentFile>({
      name: '',
      status: 'empty',
    })

  // Part 2: Bank Statements (6 months)
  const [bankStatements, setBankStatements] = useState<DocumentFile[]>([
    { name: '', status: 'empty' },
    { name: '', status: 'empty' },
    { name: '', status: 'empty' },
    { name: '', status: 'empty' },
    { name: '', status: 'empty' },
    { name: '', status: 'empty' },
  ])

  const FileUploadComponent = ({
    file,
    onFileChange,
    onRemove,
    placeholder = 'Buscar archivo',
    inputPlaceholder = '',
    accept = '.pdf',
  }: {
    file: DocumentFile
    onFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void
    onRemove: () => void
    placeholder?: string
    inputPlaceholder?: string
    accept?: string
  }) => {
    // Function to truncate filename for mobile
    const truncateFileName = (fileName: string, maxLength: number = 25) => {
      if (fileName.length <= maxLength) return fileName

      const extension = fileName.split('.').pop()
      const nameWithoutExtension = fileName.substring(
        0,
        fileName.lastIndexOf('.')
      )
      const truncatedName = nameWithoutExtension.substring(
        0,
        maxLength - extension!.length - 4
      )

      return `${truncatedName}...${extension}`
    }

    return (
      <div className="flex items-center gap-3">
        <label
          className={`flex items-center justify-center px-4 py-2 border border-gray-300 rounded-full transition-colors min-w-[140px] ${
            isUploading
              ? 'cursor-not-allowed opacity-50'
              : 'cursor-pointer hover:bg-gray-50'
          }`}
        >
          <Cloud className="w-4 h-4 text-gray-500 mr-2" />
          <span className="text-sm text-gray-700">{placeholder}</span>
          <input
            type="file"
            accept={accept}
            onChange={onFileChange}
            className="hidden"
            disabled={isUploading}
          />
        </label>

        <div className="flex-1 min-w-0">
          {file.status === 'empty' && (
            <div className="flex items-center justify-between h-10 px-3 border-gray-300 rounded bg-gray-50">
              <span className="text-sm text-gray-400">{inputPlaceholder}</span>
            </div>
          )}

          {file.status === 'pending' && (
            <div className="flex items-center justify-between h-10 px-3 border border-blue-300 rounded bg-blue-50">
              <div className="flex items-center min-w-0 flex-1">
                <Upload className="w-4 h-4 text-blue-600 mr-2 flex-shrink-0" />
                <span className="text-sm text-blue-800 truncate">
                  <span className="hidden md:inline">{file.name}</span>
                  <span className="md:hidden">
                    {truncateFileName(file.name)}
                  </span>
                  <span className="ml-1">- Listo para subir</span>
                </span>
              </div>
              <button
                onClick={onRemove}
                className="text-red-500 hover:text-red-700 ml-2 flex-shrink-0"
                disabled={isUploading}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {file.status === 'uploaded' && (
            <div className="flex items-center justify-between h-10 px-3 border border-green-300 rounded bg-green-50">
              <div className="flex items-center min-w-0 flex-1">
                <Check className="w-4 h-4 text-green-600 mr-2 flex-shrink-0" />
                <span className="text-sm text-green-800 truncate">
                  <span className="hidden md:inline">{file.name}</span>
                  <span className="md:hidden">
                    {truncateFileName(file.name)}
                  </span>
                  <span className="ml-1">- Subido</span>
                </span>
              </div>
              <button
                onClick={onRemove}
                className="text-red-500 hover:text-red-700 ml-2 flex-shrink-0"
                disabled={isUploading}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {file.status === 'error' && (
            <div className="flex items-center justify-between h-10 px-3 border border-red-300 rounded bg-red-50">
              <div className="flex items-center min-w-0 flex-1">
                <X className="w-4 h-4 text-red-600 mr-2 flex-shrink-0" />
                <span className="text-sm text-red-800 truncate">
                  <span className="hidden md:inline">{file.name}</span>
                  <span className="md:hidden">
                    {truncateFileName(file.name)}
                  </span>
                  <span className="ml-1">- Error</span>
                </span>
              </div>
              <button
                onClick={onRemove}
                className="text-red-500 hover:text-red-700 ml-2 flex-shrink-0"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>
    )
  }

  const handleFileUpload = (
    type: 'acta' | 'comprobante' | 'comprobanteOperativo' | number,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0]
    if (!file) return

    // Verificar duplicados en TODOS los documentos
    const allDocuments = [
      { name: actaConstitutiva.name, type: 'Acta Constitutiva' },
      {
        name: comprobanteDomicilio.name,
        type: 'Comprobante de domicilio fiscal',
      },
      {
        name: comprobanteDomicilioOperativo.name,
        type: 'Comprobante de domicilio operativo',
      },
      ...bankStatements.map((statement, index) => ({
        name: statement.name,
        type: `Estado de cuenta ${index + 1}`,
      })),
    ]

    // Buscar duplicados excluyendo el documento actual
    const isDuplicateFile = allDocuments.some(doc => {
      if (!doc.name) return false

      // Excluir el documento actual de la validación
      if (type === 'acta' && doc.type === 'Acta Constitutiva') return false
      if (
        type === 'comprobante' &&
        doc.type === 'Comprobante de domicilio fiscal'
      )
        return false
      if (
        type === 'comprobanteOperativo' &&
        doc.type === 'Comprobante de domicilio operativo'
      )
        return false
      if (
        typeof type === 'number' &&
        doc.type === `Estado de cuenta ${type + 1}`
      )
        return false

      return doc.name === file.name
    })

    if (isDuplicateFile) {
      const duplicateDoc = allDocuments.find(doc => doc.name === file.name)
      toast.error(
        `Ya has subido un archivo con este nombre en "${duplicateDoc?.type}". Todos los documentos deben tener nombres diferentes.`
      )
      return
    }

    let documentType: DocumentType
    let updateState: (newState: DocumentFile) => void

    if (type === 'acta') {
      documentType = DocumentType.COMPANY_REGISTRATION
      updateState = setActaConstitutiva
    } else if (type === 'comprobante') {
      documentType = DocumentType.PROOF_OF_ADDRESS
      updateState = setComprobanteDomicilio
    } else if (type === 'comprobanteOperativo') {
      documentType = DocumentType.PROOF_OF_ADDRESS_OPERATIVE
      updateState = setComprobanteDomicilioOperativo
    } else {
      documentType = DocumentType.BANK_STATEMENT
      updateState = (newState: DocumentFile) => {
        const newBankStatements = [...bankStatements]
        newBankStatements[type] = newState
        setBankStatements(newBankStatements)
      }
    }

    // Validar archivo localmente (sin subir)
    const validation = documentService.validateFile(file, documentType)
    if (!validation.valid) {
      updateState({
        name: file.name,
        status: 'error',
        error: validation.error,
        file: file,
      })
      return
    }

    // Marcar como pendiente (listo para subir)
    updateState({
      name: file.name,
      status: 'pending',
      file: file,
    })
  }

  const removeFile = async (
    type: 'acta' | 'comprobante' | 'comprobanteOperativo' | number
  ) => {
    let currentFile: DocumentFile | undefined

    if (type === 'acta') {
      currentFile = actaConstitutiva
    } else if (type === 'comprobante') {
      currentFile = comprobanteDomicilio
    } else if (type === 'comprobanteOperativo') {
      currentFile = comprobanteDomicilioOperativo
    } else {
      currentFile = bankStatements[type]
    }

    // Si el documento ya fue subido, eliminarlo del servidor
    if (currentFile?.status === 'uploaded' && currentFile?.documentId) {
      try {
        await documentService.deleteDocument(currentFile.documentId)
        toast.success('Documento eliminado')
      } catch (error) {
        console.error('Error deleting document:', error)
        toast.error('Error al eliminar el documento')
        return
      }
    }

    // Limpiar estado local
    if (type === 'acta') {
      setActaConstitutiva({ name: '', status: 'empty' })
    } else if (type === 'comprobante') {
      setComprobanteDomicilio({ name: '', status: 'empty' })
    } else if (type === 'comprobanteOperativo') {
      setComprobanteDomicilioOperativo({ name: '', status: 'empty' })
    } else {
      const newBankStatements = [...bankStatements]
      newBankStatements[type] = { name: '', status: 'empty' }
      setBankStatements(newBankStatements)
    }
  }

  const canContinuePart1 = (() => {
    // Para PFAE (Persona Física con Actividad Empresarial)
    if (personType === 'PFAE') {
      return (
        comprobanteDomicilio.status === 'uploaded' ||
        comprobanteDomicilio.status === 'pending'
      )
    }

    // Para Persona Moral (default)
    return (
      (actaConstitutiva.status === 'uploaded' ||
        actaConstitutiva.status === 'pending') &&
      (comprobanteDomicilio.status === 'uploaded' ||
        comprobanteDomicilio.status === 'pending') &&
      (isOperativeAddressSameAsFiscal ||
        comprobanteDomicilioOperativo.status === 'uploaded' ||
        comprobanteDomicilioOperativo.status === 'pending')
    )
  })()

  const canContinuePart2 = bankStatements.every(
    stmt => stmt.status === 'uploaded' || stmt.status === 'pending'
  )

  const handleContinueFromPart1 = async () => {
    if (!canContinuePart1) return

    // Preparar archivos para subir basado en el tipo de persona y domicilio operativo
    const filesToUpload = []
    const fileTypes = []

    // Agregar documento principal según el tipo de persona
    if (personType === 'PFAE') {
    } else {
      filesToUpload.push(actaConstitutiva)
      fileTypes.push('acta')
    }

    // Agregar comprobante de domicilio fiscal
    filesToUpload.push(comprobanteDomicilio)
    fileTypes.push('comprobante')

    // Agregar comprobante operativo si es necesario
    if (!isOperativeAddressSameAsFiscal && personType !== 'PFAE') {
      filesToUpload.push(comprobanteDomicilioOperativo)
      fileTypes.push('comprobanteOperativo')
    }

    // Subir archivos pendientes de la parte 1
    const uploadSuccess = await uploadPendingFiles(filesToUpload, fileTypes)

    // Solo continuar si la carga fue exitosa
    if (uploadSuccess) {
      setCurrentPart(2)
    }
  }

  const handleFinalContinue = async () => {
    if (!canContinuePart2) return

    try {
      // Subir archivos pendientes de la parte 2
      const uploadSuccess = await uploadPendingFiles(
        bankStatements,
        'bankStatements'
      )

      if (!uploadSuccess) return

      // Actualizar estatus a 'Visita Ocular'
      try {
        const prospectoQuoteId = localStorage.getItem('prospectoQuoteId')
        const token = localStorage.getItem('auth_token')
        if (prospectoQuoteId && token) {
          await apiClient.patch(
            `/cotizador/mis-cotizaciones-prospecto/${prospectoQuoteId}/estatus`,
            { estatus: 'Visita Ocular' },
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          )
          console.log('✅ Estatus "Visita Ocular" actualizado exitosamente')
        } else {
          console.warn(
            '⚠️ prospectoQuoteId o token no encontrado en localStorage'
          )
        }
      } catch (estatusError) {
        console.error(
          '❌ Error actualizando estatus "Visita Ocular":',
          estatusError
        )
      }

      // Mostrar spinner de navegación
      setIsNavigating(true)

      // Completar el flujo
      onNext()
    } catch (error) {
      console.error('Error in final continue:', error)
      toast.error('Error al completar el paso. Intenta nuevamente.')
    } finally {
      setIsNavigating(false)
    }
  }

  const uploadPendingFiles = async (
    files: DocumentFile[],
    type: string[] | string
  ): Promise<boolean> => {
    const pendingFiles = files
      .map((file, index) => ({ file, index }))
      .filter(({ file }) => file.status === 'pending' && file.file)

    if (pendingFiles.length === 0) return true

    setIsUploading(true)
    setUploadProgress(0)

    try {
      // Preparar archivos y metadata para carga por lotes
      const filesToUpload: File[] = []
      const documents: DocumentUploadItem[] = []

      pendingFiles.forEach(({ file, index }) => {
        if (file.file) {
          filesToUpload.push(file.file)

          let docType: DocumentType
          let description: string

          if (Array.isArray(type)) {
            // Parte 1: documentos legales
            if (type[index] === 'acta') {
              docType = DocumentType.COMPANY_REGISTRATION
              description = 'Acta Constitutiva'
            } else if (type[index] === 'comprobanteOperativo') {
              docType = DocumentType.PROOF_OF_ADDRESS_OPERATIVE
              description = 'Comprobante de domicilio operativo'
            } else {
              docType = DocumentType.PROOF_OF_ADDRESS
              description = 'Comprobante de domicilio fiscal'
            }
          } else {
            // Parte 2: estados de cuenta
            const months = [
              'enero2025',
              'febrero2025',
              'marzo2025',
              'abril2025',
              'mayo2025',
              'junio2025',
            ]
            docType = DocumentType.BANK_STATEMENT
            description = `Estado de cuenta ${months[index]}`
          }

          documents.push({
            type: docType,
            description,
            metadata: Array.isArray(type) ? undefined : `month_${index + 1}`,
          })
        }
      })

      // Subir todos los archivos por lotes
      const creditAppId = await authService.getCurrentCreditApplicationId()
      const response = await documentService.uploadDocuments(
        filesToUpload,
        documents,
        creditAppId
      )

      console.log('Document upload response:', response)

      // Validar que la respuesta tenga la estructura esperada
      if (!response || !response.successful) {
        throw new Error('Respuesta inválida del servidor')
      }

      // Actualizar estados basado en la respuesta
      response.successful.forEach((uploadedDoc, i) => {
        const pendingFileIndex = pendingFiles[i].index
        const newState: DocumentFile = {
          name: files[pendingFileIndex].name,
          status: 'uploaded',
          file: files[pendingFileIndex].file,
          documentId: uploadedDoc.id,
        }

        if (Array.isArray(type)) {
          // Parte 1
          if (type[pendingFileIndex] === 'acta') {
            setActaConstitutiva(newState)
          } else if (type[pendingFileIndex] === 'comprobanteOperativo') {
            setComprobanteDomicilioOperativo(newState)
          } else {
            setComprobanteDomicilio(newState)
          }
        } else {
          // Parte 2
          setBankStatements(prev => {
            const updated = [...prev]
            updated[pendingFileIndex] = newState
            return updated
          })
        }
      })

      // Marcar fallidos con error
      response.failed.forEach((failedDoc, i) => {
        const pendingFileIndex = pendingFiles[i].index
        const newState: DocumentFile = {
          name: files[pendingFileIndex].name,
          status: 'error',
          error: failedDoc.error,
          file: files[pendingFileIndex].file,
        }

        if (Array.isArray(type)) {
          // Parte 1
          if (type[pendingFileIndex] === 'acta') {
            setActaConstitutiva(newState)
          } else if (type[pendingFileIndex] === 'comprobanteOperativo') {
            setComprobanteDomicilioOperativo(newState)
          } else {
            setComprobanteDomicilio(newState)
          }
        } else {
          // Parte 2
          setBankStatements(prev => {
            const updated = [...prev]
            updated[pendingFileIndex] = newState
            return updated
          })
        }
      })

      setUploadProgress(100)

      if (response.successful.length > 0) {
        toast.success(
          `${response.successful.length} documentos subidos correctamente`
        )
      }

      if (response.failed.length > 0) {
        toast.error(
          `${response.failed.length} archivos fallaron. Revisa los errores.`
        )
        return false
      }

      return true
    } catch (error: any) {
      console.error('Error uploading batch:', error)

      // Mejor manejo de errores
      if (error.response?.data?.error?.message) {
        toast.error(error.response.data.error.message)
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message)
      } else if (error.message) {
        toast.error(`Error: ${error.message}`)
      } else {
        toast.error('Error al subir los archivos. Intenta nuevamente.')
      }

      // Log detallado para debugging
      console.log('Error details:', {
        response: error.response,
        message: error.message,
        stack: error.stack,
      })
      return false
    } finally {
      setIsUploading(false)
      setUploadProgress(0)
    }
  }

  if (currentPart === 1) {
    return (
      <>
        {/* Navigation Loading Overlay */}
        {isNavigating && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 flex items-center">
              <Loader className="w-6 h-6 text-blue-600 mr-3 animate-spin" />
              <span className="text-gray-900 font-medium">
                Navegando al siguiente paso...
              </span>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="text-center mb-4">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            Documentación
          </h1>
          <p className="text-gray-600">
            Para seguir con tu solicitud, necesitamos que cargues algunos
            documentos clave.
            <br />
            Asegúrate de que sean legibles y estén actualizados.
          </p>
        </div>

        {personType === 'PFAE' ? (
          <></>
        ) : (
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              Acta Constitutiva
            </h3>
            <div className="text-sm text-gray-600 mb-4">
              <ul className="list-disc list-inside space-y-2">
                <li className="flex items-start">
                  <span className="block w-1 h-1 bg-gray-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <span>Certificada ante Notario o Corredor Público</span>
                </li>
                <li className="flex items-start">
                  <span className="block w-1 h-1 bg-gray-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <span>
                    Debe incluir las facultades de dominio y poderes tanto de
                    accionista(s) como del Representante Legal
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="block w-1 h-1 bg-gray-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <span>
                    Registro vigente e inscrita en el Registro Público de
                    Comercio (RPC)
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="block w-1 h-1 bg-gray-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <span>
                    Protocolización de Asambleas: incluir en caso que existan
                    cambios o modificaciones (accionistas, representantes,
                    aumentos de capital, objeto, cambios de domicilio, etc.)
                  </span>
                </li>
              </ul>
              <p className="mt-4">Formato aceptado: pdf.</p>
            </div>

            <FileUploadComponent
              file={actaConstitutiva}
              onFileChange={e => handleFileUpload('acta', e)}
              onRemove={() => removeFile('acta')}
              accept=".pdf"
            />

            {actaConstitutiva.status === 'uploaded' && (
              <p className="text-xs text-green-600 mt-2">
                Archivo cargado correctamente.
              </p>
            )}

            {actaConstitutiva.status === 'error' && (
              <p className="text-xs text-red-600 mt-2">
                {actaConstitutiva.error}
              </p>
            )}
          </div>
        )}

        {/* Comprobante de Domicilio */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">
            Comprobante de domicilio fiscal
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            Debe ser no mayor a 3 meses de expedición. Asegúrate de que el
            documento sea legible y esté libre de tachaduras, enmiendas o
            manchas.
            <br />
            <p className="mt-4">Formatos aceptados: pdf, jpg, png.</p>
          </p>

          <FileUploadComponent
            file={comprobanteDomicilio}
            onFileChange={e => handleFileUpload('comprobante', e)}
            onRemove={() => removeFile('comprobante')}
            accept=".pdf,image/jpeg,image/png"
          />

          {comprobanteDomicilio.status === 'uploaded' && (
            <p className="text-xs text-green-600 mt-2">
              Archivo cargado correctamente.
            </p>
          )}

          {comprobanteDomicilio.status === 'error' && (
            <p className="text-xs text-red-600 mt-2">
              {comprobanteDomicilio.error}
            </p>
          )}
        </div>

        {/* Comprobante de Domicilio Operativo */}

        {!isOperativeAddressSameAsFiscal && personType !== 'PFAE' && (
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              Comprobante de domicilio operativo
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Debe ser no mayor a 3 meses de expedición. Asegúrate de que el
              documento sea legible y esté libre de tachaduras, enmiendas o
              manchas.
              <br />
              Formatos aceptados: pdf, jpg, png.
            </p>

            <FileUploadComponent
              file={comprobanteDomicilioOperativo}
              onFileChange={e => handleFileUpload('comprobanteOperativo', e)}
              onRemove={() => removeFile('comprobanteOperativo')}
              accept=".pdf,image/jpeg,image/png"
            />

            {comprobanteDomicilioOperativo.status === 'uploaded' && (
              <p className="text-xs text-green-600 mt-2">
                Archivo cargado correctamente.
              </p>
            )}

            {comprobanteDomicilioOperativo.status === 'error' && (
              <p className="text-xs text-red-600 mt-2">
                {comprobanteDomicilioOperativo.error}
              </p>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row justify-center gap-4">
          <Button
            onClick={handleContinueFromPart1}
            disabled={!canContinuePart1 || isUploading}
            className="px-12 py-3 min-w-[230px] sm:order-2"
          >
            {isUploading ? (
              <>
                <Loader className="w-4 h-4 mr-2 animate-spin" />
                Subiendo...
              </>
            ) : (
              'Continuar'
            )}
          </Button>

          <div className="sm:order-1">
            <ContinueLaterModal />
          </div>
        </div>
      </>
    )
  }

  // Part 2: Bank Statements
  return (
    <>
      {/* Navigation Loading Overlay */}
      {isNavigating && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 flex items-center">
            <Loader className="w-6 h-6 text-blue-600 mr-3 animate-spin" />
            <span className="text-gray-900 font-medium">
              Navegando al siguiente paso...
            </span>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="text-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Documentación</h1>
        <p className="text-gray-600">
          Para seguir con tu solicitud, necesitamos que cargues algunos
          documentos clave.
        </p>
      </div>

      {/* Estados de cuenta bancarios */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-3">
          Estados de cuenta bancarios - Últimos 6 meses
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          Carga los estados de cuenta bancarios de tu negocio de los últimos 6
          meses. Asegúrate que los descargues directo de tu banca en línea y que
          no tengan contraseña para que podamos leerlos.
          <br />
          <br />
          Formato aceptado: pdf.
        </p>

        <div className="space-y-5">
          {bankStatements.map((statement, index) => {
            const monthLabels = [
              'Mes Anterior',
              '2do mes atrás',
              '3er mes atrás',
              '4to mes atrás',
              '5to mes atrás',
              '6to mes atrás (más antiguo)',
            ]

            return (
              <div key={index}>
                <FileUploadComponent
                  file={statement}
                  onFileChange={e => handleFileUpload(index, e)}
                  onRemove={() => removeFile(index)}
                  inputPlaceholder={`${monthLabels[index]}`}
                  accept=".pdf"
                />

                {statement.status === 'uploaded' && (
                  <p className="text-xs text-green-600 mt-1">
                    Archivo cargado correctamente.
                  </p>
                )}

                {statement.status === 'error' && (
                  <p className="text-xs text-red-600 mt-1">{statement.error}</p>
                )}
              </div>
            )
          })}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row justify-center gap-4 pt-6">
        <Button
          onClick={handleFinalContinue}
          disabled={!canContinuePart2 || isUploading || isNavigating}
          className="px-12 py-3 min-w-[230px] sm:order-2"
        >
          {isUploading ? (
            <>
              <Loader className="w-4 h-4 mr-2 animate-spin" />
              Subiendo...
            </>
          ) : isNavigating ? (
            <>
              <Loader className="w-4 h-4 mr-2 animate-spin" />
              Navegando...
            </>
          ) : (
            'Continuar'
          )}
        </Button>
        <div className="sm:order-1">
          <ContinueLaterModal />
        </div>
      </div>
    </>
  )
}
